/**
 * Pickup Availability Web Component
 * Manages pickup availability display and drawer functionality
 */
if (!customElements.get('pickup-availability')) {
  customElements.define('pickup-availability', class PickupAvailability extends HTMLElement {
    constructor() {
      super();

      // Bind methods
      this.onClickRefreshList = this.onClickRefreshList.bind(this);
      this.onClickOpenDrawer = this.onClickOpenDrawer.bind(this);
      this.onClickCloseDrawer = this.onClickCloseDrawer.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);

      // Listen for variant changes
      document.addEventListener('product:variant-change', this.onVariantChange);

      // Setup delegated click listener for open button (works even if button is added dynamically)
      document.addEventListener('click', (e) => {
        const openBtn = e.target.closest('.pickup-availability-information__button');
        if (openBtn && this.contains(openBtn)) {
          this.onClickOpenDrawer(e);
        }
      });

      // Initialize if we have a variant ID
      const variantId = this.dataset.variantId;
      if (variantId) {
        // Always fetch pickup availability regardless of online availability
        this.fetchAvailability(variantId);
      }
    }

    connectedCallback() {
      // Try to initialize when component is connected to DOM
      const variantId = this.dataset.variantId;
      if (variantId && !this.hasAttribute('data-initialized')) {
        this.setAttribute('data-initialized', '');

        // Always fetch availability for any variant ID
        // Pickup availability is independent from online availability
        this.fetchAvailability(variantId);
      }

      // Setup open button if it already exists in the DOM
      setTimeout(() => {
        this.setupOpenButton();
      }, 100);

      // Also listen for variant select changes
      this.setupVariantSelectListener();
    }

    setupVariantSelectListener() {
      const productForm = this.closest('.product_form');
      if (!productForm) return;

      const selectId = productForm.dataset.selectId;
      if (!selectId) return;

      const selectElement = document.getElementById(selectId);
      if (selectElement) {
        selectElement.addEventListener('change', (e) => {
          const selectedVariantId = e.target.value;
          const productData = JSON.parse(productForm.dataset.product || '{}');
          const variant = productData.variants?.find(v => v.id.toString() === selectedVariantId);

          if (variant) {
            this.onVariantChange({
              detail: {
                variant: {
                  id: variant.id,
                  available: variant.available,
                  product: { title: productData.title }
                }
              }
            });
          }
        });
      }

      // Also intercept window.selectCallback if it exists
      if (window.selectCallback) {
        const originalCallback = window.selectCallback;
        window.selectCallback = (variant, selector) => {
          originalCallback(variant, selector);

          // Dispatch custom event for pickup availability
          document.dispatchEvent(new CustomEvent('product:variant-change', {
            detail: { variant }
          }));
        };
      }
    }

    fetchAvailability(variantId) {
      if (!variantId) {
        console.warn('[Pickup Availability] No variant ID provided');
        return;
      }

      console.log('[Pickup Availability] Fetching availability for variant:', variantId);
      
      let rootUrl = this.dataset.rootUrl;
      if (!rootUrl.endsWith("/")) {
        rootUrl = rootUrl + "/";
      }

      // First, fetch inventory data from all locations
      this.fetchInventoryData(variantId).then(inventoryData => {
        // Store inventory data for later use
        this.inventoryData = inventoryData;

        // Use variant URL directly - Shopify provides product_variant context automatically
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability&timestamp=${Date.now()}`;

        console.log('[Pickup Availability] Fetching from:', variantSectionUrl);

        fetch(variantSectionUrl, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        })
          .then(response => {
            console.log('[Pickup Availability] Response status:', response.status);
            return response.text();
          })
          .then(text => {
            console.log('[Pickup Availability] Response text length:', text.length);
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            
            if (sectionInnerHTML) {
              console.log('[Pickup Availability] Section found, rendering preview');
              this.renderPreview(sectionInnerHTML);
            } else {
              console.warn('[Pickup Availability] No section found in response');
            }
          })
          .catch((error) => {
            console.error('[Pickup Availability] Error fetching data:', error);
            const button = this.querySelector('button');
            if (button) button.removeEventListener('click', this.onClickRefreshList);
            this.renderError();
          });
      });
    }

    /**
     * Fetch inventory data for all locations using product JSON
     */
    async fetchInventoryData(variantId) {
      try {
        // Get product handle from URL or data attribute
        const productHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];
        if (!productHandle) return {};

        const response = await fetch(`/products/${productHandle}.js`);
        const productData = await response.json();
        
        // Find the variant
        const variant = productData.variants.find(v => v.id === parseInt(variantId));
        if (!variant) return {};

        console.log('[Inventory Data] Variant inventory:', variant.inventory_quantity);
        console.log('[Inventory Data] Variant available:', variant.available);
        
        return {
          totalInventory: variant.inventory_quantity || 0,
          available: variant.available
        };
      } catch (error) {
        console.error('[Inventory Data] Error:', error);
        return {};
      }
    }

    onClickRefreshList() {
      this.fetchAvailability(this.dataset.variantId);
    }

    /**
     * Handle variant change events
     */
    onVariantChange(event) {
      const { variant } = event.detail;

      if (variant && variant.id) {
        // Update data attributes
        this.dataset.variantId = variant.id;
        
        // Always fetch pickup availability regardless of online availability
        // A variant can be unavailable online but available for pickup in store
        this.fetchAvailability(variant.id);
      } else {
        // Only clear if no variant at all
        this.removeAttribute('available');
        this.innerHTML = '';

        // Clear drawer content as well
        const panel = document.getElementById('Pickup-Availability');
        if (panel) {
          panel.innerHTML = '';
        }
      }
    }

    renderError() {
      this.innerHTML = '';
      const btn = this.querySelector('button');
      if (btn) btn.addEventListener('click', this.onClickRefreshList);
    }

    /**
     * Render the pickup availability preview and drawer
     */
    renderPreview(sectionInnerHTML) {
      // Update the preview content (the visible pickup availability info)
      const previewSection = sectionInnerHTML.querySelector('.pickup-availability-preview');
      
      // Consolidated debug log
      console.log('='.repeat(70));
      console.log('[PICKUP AVAILABILITY DEBUG]');
      console.log('Variant:', previewSection?.dataset.debugVariantTitle || 'N/A');
      console.log('  - ID:', previewSection?.dataset.debugVariantId || 'N/A');
      console.log('  - Available:', previewSection?.dataset.debugVariantAvailable || 'N/A');
      console.log('  - Locations with pickup:', previewSection?.dataset.debugAvailabilitiesSize || '0');
      console.log('  - Will show pickup?:', previewSection?.dataset.debugConditionMet || 'false');
      console.log('='.repeat(70));
      
      // Check if Liquid decided to show pickup (based on is_available_display logic)
      const shouldShowPickup = previewSection?.dataset.debugConditionMet === 'true';
      const hasContent = previewSection && previewSection.innerHTML.trim().length > 0;
      
      console.log('[Render Preview] shouldShowPickup:', shouldShowPickup);
      console.log('[Render Preview] hasContent:', hasContent);
      console.log('[Render Preview] previewSection:', previewSection);
      
      if (previewSection && hasContent) {
        // Set available attribute if there's content to show
        this.setAttribute('available', '');
        
        // Extract the inner content from previewSection (the actual pickup-availability-information element)
        const innerContent = previewSection.querySelector('.pickup-availability-information');
        if (innerContent) {
          console.log('[Render Preview] Found pickup-availability-information, inserting content');
          this.innerHTML = innerContent.outerHTML;
          console.log('[Render Preview] Content inserted, element now has:', this.innerHTML.length, 'characters');
          console.log('[Render Preview] Element is visible:', this.offsetHeight > 0 || this.offsetWidth > 0);
        } else {
          // Fallback: use the innerHTML if the structure is different
          console.log('[Render Preview] No pickup-availability-information found, using previewSection.innerHTML');
          this.innerHTML = previewSection.innerHTML;
        }

        // Setup open button immediately after updating innerHTML
        setTimeout(() => {
          this.setupOpenButton();
        }, 0);
      } else {
        // Clear if no content
        console.log('[Render Preview] No content to show, clearing element');
        this.removeAttribute('available');
        this.innerHTML = '';
      }

      // Update drawer content in the panel
      const panel = document.getElementById('Pickup-Availability');
      const newDrawerContent = sectionInnerHTML.querySelector('.side-availability');

      if (panel && newDrawerContent) {
        // Completely clear and replace panel content
        panel.innerHTML = '';

        // Force a reflow to ensure clearing is complete
        panel.offsetHeight;

        // Set new content
        panel.innerHTML = newDrawerContent.innerHTML;

        // Process pickup availability logic
        this.processPickupAvailability(panel, previewSection);

        // Setup drawer functionality
        this.setupDrawer(panel);
      } else if (panel) {
        // Clear drawer if no content
        panel.innerHTML = '';
      }
    }

    /**
     * Process pickup availability and handle transfer logic
     */
    processPickupAvailability(panel, previewSection) {
      if (!panel) return;

      // Get variant data from preview section
      const variantAvailable = previewSection?.dataset.debugVariantAvailable === 'true';
      const shouldShowPickup = previewSection?.dataset.debugConditionMet === 'true';
      const variantId = previewSection?.dataset.debugVariantId;

      console.log('[Pickup Availability] Processing locations...');
      console.log('  - Variant Available:', variantAvailable);
      console.log('  - Should show pickup (from Liquid):', shouldShowPickup);
      console.log('  - Variant ID:', variantId);
      
      // Process even if shouldShowPickup is false, as we might need to update the drawer
      // The preview visibility is controlled by renderPreview, not here

      // Get all location items (including hidden ones from Liquid)
      const allLocationItems = panel.querySelectorAll('.pickup-availability-list__item');
      console.log(`  Total location items found: ${allLocationItems.length}`);
      
      const locationItems = Array.from(allLocationItems).filter(item => {
        const name = item.querySelector('.pickup-availability-list__location')?.textContent.trim().toLowerCase() || '';
        // Filter out non-pickup locations (online, web, etc)
        return !name.includes('online') && !name.includes('web') && !name.includes('website') && !name.includes('virtual');
      });
      
      console.log(`  Location items after filtering: ${locationItems.length}`);
      
      // Separate locations by type
      const locations = {
        retail: [],
        warehouse: [],
        supplier: []
      };

      locationItems.forEach((item) => {
        const locationName = item.querySelector('.pickup-availability-list__location')?.textContent.trim() || '';
        const locationNameLower = locationName.toLowerCase();
        const statusElement = item.querySelector('.pickup-availability-list__status');
        const isInStock = statusElement?.classList.contains('pickup-availability-list__status--available');
        
        const locationData = {
          element: item,
          name: locationName,
          nameLower: locationNameLower,
          statusElement: statusElement,
          isInStock: isInStock
        };

        if (locationNameLower.includes('supplier') || locationNameLower.includes('fulfillment')) {
          locations.supplier.push(locationData);
        } else if (locationNameLower.includes('warehouse')) {
          locations.warehouse.push(locationData);
        } else {
          locations.retail.push(locationData);
        }

        console.log(`  Location: ${locationName}`);
        console.log(`    - Type: ${locationNameLower.includes('supplier') ? 'Supplier' : locationNameLower.includes('warehouse') ? 'Warehouse' : 'Retail'}`);
        console.log(`    - Shows "In stock": ${isInStock}`);
      });

      // Check if supplier has stock
      // Since Shopify doesn't include supplier in store_availabilities, we infer it:
      // Total inventory - (retail stock + warehouse stock) = supplier stock
      const supplierHasStock = locations.supplier.some(loc => loc.isInStock);
      
      const totalInventory = this.inventoryData?.totalInventory || 0;
      const retailStock = locations.retail.filter(loc => loc.isInStock).length;
      const warehouseStock = locations.warehouse.filter(loc => loc.isInStock).length;
      const accountedStock = retailStock + warehouseStock;
      const inferredSupplierStock = totalInventory > accountedStock;
      
      const hasSupplierStock = supplierHasStock || inferredSupplierStock;
      
      console.log(`  Total inventory: ${totalInventory}`);
      console.log(`  Retail locations with stock: ${retailStock}`);
      console.log(`  Warehouse locations with stock: ${warehouseStock}`);
      console.log(`  Accounted stock: ${accountedStock}`);
      console.log(`  Supplier has stock (detected): ${supplierHasStock}`);
      console.log(`  Supplier has stock (inferred): ${inferredSupplierStock}`);
      console.log(`  Final supplier stock status: ${hasSupplierStock}`);

      // Apply transfer logic for warehouse
      locations.warehouse.forEach(warehouse => {
        if (!warehouse.isInStock && hasSupplierStock && variantAvailable) {
          // Warehouse doesn't have stock, but supplier does - mark warehouse as available
          console.log(`  🔄 Transfer logic: ${warehouse.name} can receive from supplier`);
          
          if (warehouse.statusElement) {
            warehouse.statusElement.classList.remove('pickup-availability-list__status--unavailable');
            warehouse.statusElement.classList.add('pickup-availability-list__status--available');
            warehouse.statusElement.textContent = '— In stock';
          }

          // Show pickup availability text
          const pickupPreview = warehouse.element.querySelector('.pickup-availability-preview');
          if (pickupPreview) {
            pickupPreview.style.display = '';
          }
        }
      });

      // Detect "Sell when out of stock" scenario
      // This should only trigger when:
      // 1. All visible locations show "in stock" (but this might be because of SWOOS setting)
      // 2. Total inventory is 0 (meaning there's no real stock, just SWOOS allowing sales)
      // 3. Multiple locations exist
      const visibleLocations = [...locations.retail, ...locations.warehouse];
      const allShowInStock = visibleLocations.every(loc => loc.isInStock);
      const hasMultipleVisibleLocations = visibleLocations.length > 1;
      const hasRealInventory = totalInventory > 0;

      // Only hide if we detect SWOOS: all locations show stock but total inventory is 0
      // If there's real inventory, don't hide even if all locations show stock
      // Use totalInventory === 0 directly to be more explicit
      if (variantAvailable && allShowInStock && hasMultipleVisibleLocations && totalInventory === 0 && !supplierHasStock) {
        console.log('  ⚠️ "Sell when out of stock" detected - hiding pickup availability');
        console.log('  - Total inventory:', totalInventory);
        console.log('  - All locations show in stock but no real inventory (SWOOS)');
        
        this.removeAttribute('available');
        this.innerHTML = '';
        
        if (panel) {
          panel.innerHTML = '';
        }
        
        return;
      } else if (allShowInStock && hasMultipleVisibleLocations && hasRealInventory) {
        console.log('  ✓ All locations show in stock with real inventory - keeping pickup visible');
        console.log('  - Total inventory:', totalInventory);
      }

      console.log('  ✓ Pickup availability processed successfully');
    }

    /**
     * Setup open button for drawer
     */
    setupOpenButton() {
      // Setup open button - look for the "View store information" button
      const openBtn = this.querySelector('.pickup-availability-information__button');
      if (openBtn) {
        // Remove any existing listeners first
        openBtn.removeEventListener('click', this.onClickOpenDrawer);
        openBtn.addEventListener('click', this.onClickOpenDrawer);
      } else {
        // Fallback: try to find any button
        const fallbackBtn = this.querySelector('button');
        if (fallbackBtn) {
          fallbackBtn.removeEventListener('click', this.onClickOpenDrawer);
          fallbackBtn.addEventListener('click', this.onClickOpenDrawer);
        }
      }
    }

    /**
     * Setup drawer open/close functionality
     */
    setupDrawer(panel) {
      // Setup open button
      this.setupOpenButton();

      // Setup close button
      const closeBtn = panel.querySelector('.pickup-availability-drawer-button');
      if (closeBtn) {
        closeBtn.removeEventListener('click', this.onClickCloseDrawer);
        closeBtn.addEventListener('click', this.onClickCloseDrawer);
      }
    }

    /**
     * Handle drawer open
     */
    onClickOpenDrawer(evt) {
      evt.preventDefault();
      evt.stopPropagation();

      const panel = document.getElementById('Pickup-Availability');
      if (panel) {
        panel.classList.add('active');
        document.body.classList.add('open-cc');
      }
    }

    /**
     * Handle drawer close
     */
    onClickCloseDrawer() {
      const panel = document.getElementById('Pickup-Availability');
      if (panel) {
        panel.classList.remove('active');
        document.body.classList.remove('open-cc');
      }
    }

    /**
     * Cleanup when component is removed
     */
    disconnectedCallback() {
      document.removeEventListener('product:variant-change', this.onVariantChange);
    }
  });
}